// useNodeFormFields.js

import { useMemo } from 'react';

import { getResolvedFields } from 'utilities/NodeType';
import { useNodeTypes } from 'context/NodeTypesContext';
import { FORM_EXCLUDED_FIELD_TYPES } from 'services/forms/FormModel';
import { resolveDisplaySchemaFields } from 'views/utils/displaySchemaFields';

const BUILTIN_SCHEMA_FIELD_TYPES = new Set([
  'color',
  'cover',
  'subtitle',
  'dates',
  'location',
  'tags',
]);

export default function useNodeFormFields({
  nodeTypeKey,
  language,
  typeDef,
  overrideFields,
  excludedFieldTypes,
  node = null,
}) {
  const { getRevision } = useNodeTypes();
  const nodeTypesRevision = getRevision();

  const excludedTypes = useMemo(() => {
    const types = new Set(FORM_EXCLUDED_FIELD_TYPES);
    if (Array.isArray(excludedFieldTypes)) {
      excludedFieldTypes.forEach((type) => {
        if (typeof type === 'string' && type.trim()) types.add(type.trim());
      });
    }
    return types;
  }, [excludedFieldTypes]);

  const resolvedFields = useMemo(() => {
    void nodeTypesRevision;
    const fromTypeDef = resolveDisplaySchemaFields(typeDef, { includeHidden: false });
    if (fromTypeDef.length) return fromTypeDef;
    return getResolvedFields(nodeTypeKey, { includeHidden: false, excludeSecured: true });
  }, [nodeTypeKey, nodeTypesRevision, typeDef]);

  const sourceFields = useMemo(() => {
    if (Array.isArray(overrideFields)) return overrideFields;
    return resolvedFields;
  }, [overrideFields, resolvedFields]);

  const fields = useMemo(
    () => sourceFields.filter((f) => {
      if (!f || f.hidden === true || f.secured === true) return false;
      if (!f.name || !f.type) return false;
      if (excludedTypes.has(f.type)) return false;
      if (BUILTIN_SCHEMA_FIELD_TYPES.has(f.type) && f.source && f.source !== nodeTypeKey) return false;
      return Boolean(f.label);
    }),
    [sourceFields, nodeTypeKey, excludedTypes],
  );
  return { fields, resolvedFields };
}
